<script setup>
import { computed, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import AiIdentity from 'dashboard/components-next/ai/AiIdentity.vue';
import AiUsageReceipt from 'dashboard/components-next/ai/AiUsageReceipt.vue';
import InvoicesAPI from 'dashboard/api/talkviaInventoryInvoices';

// Entrada de estoque pela nota fiscal: o XML da NF-e é lido pelo sistema; o PDF (DANFE) ou
// a foto, pela Lia. Tudo passa por conferência antes de lançar.
const props = defineProps({
  products: { type: Array, default: () => [] },
  suppliers: { type: Array, default: () => [] },
});
const emit = defineEmits(['imported']);

const NEW_PRODUCT = 'new';
const dialog = ref();
const step = ref('upload');
const reading = ref('');
const saving = ref(false);
const error = ref('');
const invoice = ref(null);
const usage = ref(null);
const items = ref([]);
const supplierChoice = ref('');
const xmlInput = ref();
const aiInput = ref();

const reset = () => {
  step.value = 'upload';
  reading.value = '';
  error.value = '';
  invoice.value = null;
  usage.value = null;
  items.value = [];
  supplierChoice.value = '';
};
const open = () => {
  reset();
  dialog.value.open();
};
defineExpose({ open });

const money = value =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
const loadPreview = data => {
  invoice.value = data.invoice;
  usage.value = data.usage || null;
  items.value = data.invoice.items.map(item => ({
    ...item,
    choice: item.product ? String(item.product.id) : NEW_PRODUCT,
    newName: item.description,
    salePrice: '',
  }));
  // Fornecedor já cadastrado com o mesmo CNPJ; senão, sugere cadastrar o emitente.
  const match = data.invoice.supplier?.match;
  if (match) supplierChoice.value = String(match.id);
  else supplierChoice.value = data.invoice.supplier?.name ? NEW_PRODUCT : '';
  step.value = 'review';
};
const read = async (kind, file) => {
  if (!file) return;
  error.value = '';
  reading.value = kind;
  try {
    const { data } =
      kind === 'xml'
        ? await InvoicesAPI.preview(file)
        : await InvoicesAPI.readWithAi(file);
    loadPreview(data);
  } catch (failure) {
    usage.value = failure.response?.data?.usage || null;
    error.value =
      failure.response?.data?.error ||
      'Não foi possível ler a nota. Tente novamente.';
  } finally {
    reading.value = '';
    if (xmlInput.value) xmlInput.value.value = '';
    if (aiInput.value) aiInput.value.value = '';
  }
};

const productOptions = computed(() => [
  {
    value: NEW_PRODUCT,
    label: 'Cadastrar como produto novo',
    icon: 'i-hugeicons-add-01',
  },
  ...props.products.map(product => ({
    value: String(product.id),
    label: product.name,
    icon: 'i-hugeicons-package',
  })),
]);
const supplierOptions = computed(() => [
  { value: '', label: 'Sem fornecedor', icon: 'i-hugeicons-cancel-01' },
  ...(invoice.value?.supplier?.name
    ? [
        {
          value: NEW_PRODUCT,
          label: `Cadastrar ${invoice.value.supplier.name}`,
          icon: 'i-hugeicons-add-01',
        },
      ]
    : []),
  ...props.suppliers.map(supplier => ({
    value: String(supplier.id),
    label: supplier.name,
    icon: 'i-hugeicons-truck-delivery',
  })),
]);
const newProducts = computed(
  () => items.value.filter(item => item.choice === NEW_PRODUCT).length
);
const total = computed(() =>
  items.value.reduce(
    (sum, item) =>
      sum + Number(item.quantity || 0) * Number(item.unit_cost || 0),
    0
  )
);

const confirm = async () => {
  if (saving.value) return;
  saving.value = true;
  error.value = '';
  try {
    const supplier =
      supplierChoice.value === NEW_PRODUCT
        ? {
            create: true,
            name: invoice.value.supplier.name,
            document: invoice.value.supplier.document,
          }
        : { id: supplierChoice.value || undefined };
    const { data } = await InvoicesAPI.create({
      access_key: invoice.value.access_key,
      number: invoice.value.number,
      supplier,
      items: items.value.map(item => ({
        number: item.number,
        code: item.code,
        barcode: item.barcode,
        description: item.description,
        unit: item.unit,
        quantity: item.quantity,
        unit_cost: item.unit_cost,
        ...(item.choice === NEW_PRODUCT
          ? {
              new_product: {
                name: item.newName,
                unit: item.unit,
                sale_price: item.salePrice,
              },
            }
          : { product_id: item.choice }),
      })),
    });
    useAlert(
      `Entrada lançada: ${data.movements} ${data.movements === 1 ? 'item' : 'itens'}${data.created_products ? `, ${data.created_products} produto(s) novo(s)` : ''}. Custos atualizados pela nota.`,
      'success'
    );
    emit('imported');
    dialog.value.close();
  } catch (failure) {
    error.value =
      failure.response?.data?.error ||
      'Não foi possível lançar a entrada. Tente novamente.';
  } finally {
    saving.value = false;
  }
};
const INPUT =
  'w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand';
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Dialog
    ref="dialog"
    show-close-icon
    overflow-y-auto
    sticky-header-footer
    :width="step === 'review' ? '5xl' : 'xl'"
    title="Entrada por nota fiscal"
    description="Lance os produtos da nota no estoque. O custo de cada produto é atualizado pelo valor da nota."
    :show-confirm-button="false"
  >
    <p v-if="error" role="alert" class="mb-3 text-sm text-n-ruby-11">
      {{ error }}
    </p>

    <div v-if="step === 'upload'" class="grid gap-3 sm:grid-cols-2">
      <label
        class="flex cursor-pointer flex-col gap-2 rounded-xl border border-n-weak p-4 transition hover:border-n-brand/50 hover:bg-n-alpha-1"
        :class="{ 'pointer-events-none opacity-60': reading }"
      >
        <span
          class="grid size-10 place-content-center rounded-lg bg-n-teal-3 text-n-teal-11"
        >
          <span class="i-hugeicons-file-code size-5" />
        </span>
        <strong class="text-sm text-n-slate-12">XML da NF-e</strong>
        <span class="text-xs text-n-slate-11">
          Leitura exata do arquivo da nota. Não consome créditos.
        </span>
        <span class="mt-auto text-sm font-semibold text-n-brand">
          {{ reading === 'xml' ? 'Lendo...' : 'Escolher XML' }}
        </span>
        <input
          ref="xmlInput"
          type="file"
          accept=".xml,text/xml,application/xml"
          class="sr-only"
          @change="read('xml', $event.target.files[0])"
        />
      </label>
      <label
        class="flex cursor-pointer flex-col gap-2 rounded-xl border border-n-weak p-4 transition hover:border-n-brand/50 hover:bg-n-alpha-1"
        :class="{ 'pointer-events-none opacity-60': reading }"
      >
        <AiIdentity class="size-10" :animated="reading === 'ai'" />
        <strong class="text-sm text-n-slate-12">PDF ou foto com a Lia</strong>
        <span class="text-xs text-n-slate-11">
          A Lia lê o DANFE em PDF ou a foto da nota. Consome créditos de IA.
        </span>
        <span class="mt-auto text-sm font-semibold text-n-brand">
          {{
            reading === 'ai' ? 'A Lia está lendo...' : 'Escolher PDF ou foto'
          }}
        </span>
        <input
          ref="aiInput"
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          class="sr-only"
          @change="read('ai', $event.target.files[0])"
        />
      </label>
      <AiUsageReceipt v-if="usage" class="sm:col-span-2" :usage="usage" />
    </div>

    <div v-else-if="invoice" class="space-y-4">
      <div
        v-if="invoice.already_imported"
        role="alert"
        class="rounded-lg border border-n-amber-6 bg-n-amber-2 p-3 text-sm text-n-amber-12"
      >
        Esta nota já foi lançada no estoque. Confirmar de novo não é permitido.
      </div>
      <div
        v-if="invoice.source === 'lia'"
        class="flex items-start gap-2 rounded-lg bg-n-alpha-1 p-3 text-xs text-n-slate-11"
      >
        <AiIdentity class="size-5 shrink-0" :animated="false" />
        Lido pela Lia. Confira quantidades e valores antes de lançar.
      </div>
      <div
        class="grid gap-3 rounded-xl border border-n-weak p-4 sm:grid-cols-3"
      >
        <div>
          <p class="m-0 text-xs text-n-slate-11">Nota</p>
          <p class="m-0 text-sm font-semibold text-n-slate-12">
            {{ invoice.number ? `NF-e ${invoice.number}` : 'Sem número' }}
            <span v-if="invoice.series" class="font-normal text-n-slate-11">
              · série {{ invoice.series }}
            </span>
          </p>
        </div>
        <div>
          <p class="m-0 text-xs text-n-slate-11">Emitente</p>
          <p class="m-0 truncate text-sm font-semibold text-n-slate-12">
            {{ invoice.supplier?.name || 'Não identificado' }}
          </p>
          <p class="m-0 text-xs tabular-nums text-n-slate-11">
            {{ invoice.supplier?.document }}
          </p>
        </div>
        <div class="grid gap-1">
          <p class="m-0 text-xs text-n-slate-11">Fornecedor no estoque</p>
          <ComboBox
            v-model="supplierChoice"
            :options="supplierOptions"
            placeholder="Sem fornecedor"
            teleport-to-body
          />
        </div>
      </div>

      <div class="overflow-x-auto rounded-xl border border-n-weak">
        <table class="w-full min-w-[760px] text-sm">
          <thead class="bg-n-alpha-1 text-xs text-n-slate-11">
            <tr>
              <th class="px-3 py-2 text-left font-medium">Item da nota</th>
              <th class="w-24 px-3 py-2 text-left font-medium">Qtd.</th>
              <th class="w-28 px-3 py-2 text-left font-medium">Custo unit.</th>
              <th class="w-64 px-3 py-2 text-left font-medium">
                Produto no estoque
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="item in items" :key="item.number" class="align-top">
              <td class="px-3 py-2">
                <p class="m-0 font-medium text-n-slate-12">
                  {{ item.description }}
                </p>
                <p class="m-0 text-xs text-n-slate-10">
                  {{
                    [item.code && `Cód. ${item.code}`, item.barcode, item.unit]
                      .filter(Boolean)
                      .join(' · ')
                  }}
                </p>
              </td>
              <td class="px-3 py-2">
                <input
                  v-model="item.quantity"
                  type="number"
                  min="0"
                  step="any"
                  :class="INPUT"
                  aria-label="Quantidade"
                />
              </td>
              <td class="px-3 py-2">
                <input
                  v-model="item.unit_cost"
                  type="number"
                  min="0"
                  step="0.01"
                  :class="INPUT"
                  aria-label="Custo unitário"
                />
              </td>
              <td class="space-y-1.5 px-3 py-2">
                <ComboBox
                  v-model="item.choice"
                  :options="productOptions"
                  search-placeholder="Buscar produto"
                  teleport-to-body
                />
                <template v-if="item.choice === 'new'">
                  <input
                    v-model="item.newName"
                    maxlength="250"
                    :class="INPUT"
                    placeholder="Nome do produto"
                    aria-label="Nome do produto novo"
                  />
                  <input
                    v-model="item.salePrice"
                    type="number"
                    min="0"
                    step="0.01"
                    :class="INPUT"
                    placeholder="Preço de venda (opcional)"
                    aria-label="Preço de venda"
                  />
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <AiUsageReceipt v-if="usage" :usage="usage" />
    </div>

    <template v-if="step === 'review'" #footer>
      <div class="flex w-full flex-wrap items-center gap-3">
        <Button
          label="Trocar arquivo"
          variant="ghost"
          color="slate"
          :disabled="saving"
          @click="reset"
        />
        <p class="m-0 text-sm text-n-slate-11 sm:ml-auto">
          {{ items.length }} {{ items.length === 1 ? 'item' : 'itens' }} ·
          <strong class="text-n-slate-12">{{ money(total) }}</strong>
          <span v-if="newProducts">
            · {{ newProducts }} produto(s) novo(s)
          </span>
        </p>
        <Button
          label="Lançar entrada no estoque"
          icon="i-hugeicons-package-receive"
          :is-loading="saving"
          :disabled="saving || invoice?.already_imported || !items.length"
          @click="confirm"
        />
      </div>
    </template>
  </Dialog>
</template>
